import { KeyboardEventHandler, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { FilterButton, FilterMobileLink, FiltersMobileButton, FiltersMobileList,
  FiltersMobileWrapper, FiltersWrapper, SearchBarWrapper } from './styles'
import { useTranslation } from 'react-i18next'
import { Input } from 'antd'
import { ReactComponent as SearchIcon } from './assets/search.svg'
import { SlidersOutlined } from '@ant-design/icons'
import { ListDomiciliationsPrefilter } from 'pages/ClientsList/queries'
import { SetState } from 'globals'
import _ from 'lodash'

interface IProps {
  hideFilters?: boolean,
  activeFilter?: ListDomiciliationsPrefilter,
  onChangeFilter?: (filter: ListDomiciliationsPrefilter) => void,
  searchOnTheFly?: boolean | undefined,
  value?: string,
  placeholder?: string,
  marginBottom?: boolean | undefined,
  height?: string | undefined,
  backgroundColor?: string,
  onChange?: (value: string) => void,
  onSearch: (searchText: string) => void
}

const SearchBar = (props: IProps) => {
  const { t } = useTranslation()
  const { hideFilters, activeFilter, onChangeFilter, searchOnTheFly, onSearch } = props
  const placeholder = props.placeholder || t('search.placeholder')
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false)
  const _onFilterClick = onFilterClick(setShowMobileFilters, onChangeFilter)
  const queryUrl = new URLSearchParams(location.search)
  // Created once: a debounce rebuilt on every render (each keystroke) never debounces.
  // The ref lets it call the latest onSearch, whose closure holds the current filter.
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch
  const lastSearchRef = useRef<string | undefined>(undefined)
  const onSearchDebounced = useMemo(() => _.debounce((text: string) => {
    lastSearchRef.current = text
    onSearchRef.current(text)
  }, 1000), [])
  useEffect(() => () => onSearchDebounced.cancel(), [onSearchDebounced])

  return (
    <SearchBarWrapper
      marginBottom={!!props.marginBottom}
      height={props.height}
      backgroundColor={props.backgroundColor}
    >
      <Input
        autoFocus={queryUrl.get('search')? true : false}
        defaultValue={queryUrl.get('search') || props.value || ''}
        placeholder={placeholder}
        prefix={<SearchIcon />}
        onChange={(e) => {
          props.onChange ? props.onChange(e.target.value) : null
        }}
        onKeyUp={onKeyPress(onSearchDebounced, lastSearchRef, searchOnTheFly)}
      />

      {!hideFilters &&
        <>
          <FiltersWrapper>
            <FilterButton
              active={activeFilter === 'all'}
              onClick={_onFilterClick('all')}
            >
              {t('search.filters.all')}
            </FilterButton>

            <FilterButton
              active={activeFilter === 'activated'}
              onClick={_onFilterClick('activated')}
            >
              {t('search.filters.actives')}
            </FilterButton>

            <FilterButton
              active={activeFilter === 'pending_partner_check'}
              onClick={_onFilterClick('pending_partner_check')}
            >
              {t('search.filters.waitings')}
            </FilterButton>

            <FilterButton
              active={activeFilter === 'canceled'}
              onClick={_onFilterClick('canceled')}
            >
              {t('search.filters.terminated')}
            </FilterButton>

            <FilterButton
              active={activeFilter === 'npai'}
              onClick={_onFilterClick('npai')}
            >
              {t('search.filters.npai')}
            </FilterButton>
          </FiltersWrapper>

          <FiltersMobileWrapper>
            <FiltersMobileButton
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              active={showMobileFilters}
            >
              <SlidersOutlined />
            </FiltersMobileButton>

            <FiltersMobileList
              show={showMobileFilters}
            >
              <FilterMobileLink
                active={activeFilter === 'all'}
                onClick={_onFilterClick('all')}
              >
                {t('search.filters.all')}
              </FilterMobileLink>

              <FilterMobileLink
                active={activeFilter === 'activated'}
                onClick={_onFilterClick('activated')}
              >
                {t('search.filters.actives')}
              </FilterMobileLink>

              <FilterMobileLink
                active={activeFilter === 'pending_partner_check'}
                onClick={_onFilterClick('pending_partner_check')}
              >
                {t('search.filters.waitings')}
              </FilterMobileLink>

              <FilterMobileLink
                active={activeFilter === 'canceled'}
                onClick={_onFilterClick('canceled')}
              >
                {t('search.filters.terminated')}
              </FilterMobileLink>
            </FiltersMobileList>
          </FiltersMobileWrapper>
        </>
      }
    </SearchBarWrapper>
  )
}

const onFilterClick = (
    setShowMobileFilters: SetState<boolean>,
    onChangeFilter?: (filter: ListDomiciliationsPrefilter) => void
) => (filter: ListDomiciliationsPrefilter) => () => {
  setShowMobileFilters(false)

  onChangeFilter && onChangeFilter(filter)
}

const onKeyPress = (
    onSearchDebounced: _.DebouncedFunc<(searchText: string) => void>,
    lastSearchRef: MutableRefObject<string | undefined>,
    searchOnTheFly: boolean | undefined): KeyboardEventHandler<HTMLInputElement> => (event) => {
  const text = event.currentTarget.value

  if (event.key === 'Enter') {
    onSearchDebounced(text)
    onSearchDebounced.flush()
  } else if (searchOnTheFly && text !== (lastSearchRef.current ?? '')) {
    // Arrows, Shift, Tab... do not change the text: no new search.
    onSearchDebounced(text)
  }
}

export default SearchBar
